import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { useAuth } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { BottomNav } from "@/components/BottomNav";
import { MusicPlayer } from "@/components/MusicPlayer";
import AppPrompts from "@/components/AppPrompts";
import ProfileCompletionModal from "@/components/ProfileCompletionModal";
import WelcomeOverlay from "@/components/WelcomeOverlay";
import EmailCollectionModal from "@/components/EmailCollectionModal";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/config/firebase";

// Routes that should have a clean shell (no nav, no prompts, no modals)
const isCleanShellRoute = (pathname) => {
  return pathname.includes('/predictions/stage/') || pathname.startsWith('/admin/predictions');
};

const AuthedNav = () => { 
  const { user } = useAuth(); 
  const location = useLocation();
  
  // Hide nav on Stage route (clean shell)
  if (isCleanShellRoute(location.pathname)) return null;
  
  return user ? <BottomNav /> : null; 
};

// Routes where prompts/modals must never interrupt (live game)
const isNoPromptRoute = (pathname) => isCleanShellRoute(pathname) || pathname.startsWith('/predictions');

const AuthedPrompts = () => { 
  const { user } = useAuth(); 
  const location = useLocation();
  
  // Hide prompts on Stage route (clean shell) and during the live game
  if (isNoPromptRoute(location.pathname)) return null;
  
  return user ? <AppPrompts /> : null; 
};

// App-Hintergrundmusik: nur eingeloggt, nicht auf Stage/Live (dort läuft Video/Spielton)
const AuthedMusic = () => {
  const { user } = useAuth();
  const location = useLocation();
  if (isNoPromptRoute(location.pathname)) return null;
  return user ? <MusicPlayer /> : null;
};

// Profile completion checker component
const ProfileCompletionChecker = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
          // Deaktiviert: Spieler sollen am Spielabend nicht durch Größe/Gewicht/Position
          // geblockt werden. Einziger Pflichtschritt ist die E-Mail (EmailChecker).
          setShowModal(false);
        }
      } catch (err) {
        console.error('Error checking profile:', err);
      }
      setLoading(false);
    };

    checkProfile();
  }, [user]);

  // Don't show modal on Stage route (clean shell) or during the live game
  if (isNoPromptRoute(location.pathname)) return null;
  if (!user || loading || !showModal) return null;

  return (
    <ProfileCompletionModal
      user={user}
      userData={userData}
      onComplete={() => setShowModal(false)}
    />
  );
};

// Willkommens-Moment nach Login (einmal pro Session): großer Kopf + Name
const WelcomeChecker = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [welcome, setWelcome] = useState(null);

  useEffect(() => {
    const run = async () => {
      if (!user) { setWelcome(null); return; }
      const key = `rs5_welcome_shown_${user.uid}`;
      if (sessionStorage.getItem(key)) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.exists() ? userDoc.data() : {};
        const name = data.display_name || user.displayName || data.name || (user.email ? user.email.split('@')[0] : 'Spieler');
        sessionStorage.setItem(key, '1');
        setWelcome({ name, photoUrl: data.photo_url || '' });
      } catch (err) {
        console.error('Welcome check failed:', err);
      }
    };
    run();
  }, [user]);

  if (isCleanShellRoute(location.pathname)) return null;
  if (!user || !welcome) return null;

  return (
    <WelcomeOverlay
      name={welcome.name}
      photoUrl={welcome.photoUrl}
      onClose={() => setWelcome(null)}
    />
  );
};

// Pflicht-E-Mail beim nächsten Login (Flag needsEmailUpdate)
const EmailChecker = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    const run = async () => {
      if (!user) { setShow(false); return; }
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = snap.data();
          setUserData(data);
          setShow(data.needsEmailUpdate === true);
        }
      } catch (err) {
        console.error('Email check failed:', err);
      }
    };
    run();
  }, [user]);

  if (isCleanShellRoute(location.pathname)) return null;
  if (!user || !show) return null;

  return (
    <EmailCollectionModal user={user} userData={userData} onComplete={() => setShow(false)} />
  );
};


// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Calendar from "@/pages/Calendar";
import Kiosk from "@/pages/Kiosk";
import Stats from "@/pages/Stats";
import Bets from "@/pages/Bets";
import BetsLeaderboard from "@/pages/BetsLeaderboard";
import ImportRoster from "@/pages/ImportRoster";
import Vote from "@/pages/Vote";
import Chronicle from "@/pages/Chronicle";
import Glossar from "@/pages/Glossar";
import Liga from "@/pages/Liga";
import EventsImport from "@/pages/EventsImport";
import Profile from "@/pages/Profile";
import Admin from "@/pages/Admin";
import AdminUsers from "@/pages/AdminUsers";
import StatsImport from "@/pages/StatsImport";
import RosterClean from "@/pages/RosterClean";
import ShowerGelMVP from "@/pages/ShowerGelMVP";
import Tournaments from "@/pages/Tournaments";
import TournamentNew from "@/pages/TournamentNew";
import TournamentDetail from "@/pages/TournamentDetail";
import Chat from "@/pages/Chat";
import Gallery from "@/pages/Gallery";
import Media from "@/pages/Media";
import MeinAbend from "@/pages/MeinAbend";
import MarketOpsPanel from "@/pages/predictions/admin/MarketOpsPanel";

// Predictions Feature
import PredictionsHub from "@/pages/predictions/PredictionsHub";
import PredictionsLive from "@/pages/predictions/PredictionsLive";
import PredictionsStage from "@/pages/predictions/PredictionsStage";
import PredictionsAdmin from "@/pages/predictions/admin/PredictionsAdmin";
import WarmRainPrint from "@/pages/predictions/admin/WarmRainPrint";
import PredictionsHost from "@/pages/predictions/admin/PredictionsHost";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />

            <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/calendar" element={<ProtectedRoute><Calendar /></ProtectedRoute>} />
            <Route path="/kiosk" element={<ProtectedRoute><Kiosk /></ProtectedRoute>} />
            <Route path="/stats" element={<ProtectedRoute><Stats /></ProtectedRoute>} />
            <Route path="/stats/shower-gel-mvp" element={<ProtectedRoute><ShowerGelMVP /></ProtectedRoute>} />
            <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
            <Route path="/tournaments/new" element={<ProtectedRoute><TournamentNew /></ProtectedRoute>} />
            <Route path="/tournaments/:id" element={<ProtectedRoute><TournamentDetail /></ProtectedRoute>} />
            <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
            <Route path="/gallery" element={<ProtectedRoute><Gallery /></ProtectedRoute>} />
            <Route path="/bets" element={<ProtectedRoute><Bets /></ProtectedRoute>} />
            <Route path="/bets/leaderboard" element={<ProtectedRoute><BetsLeaderboard /></ProtectedRoute>} />
            <Route path="/import" element={<ProtectedRoute><ImportRoster /></ProtectedRoute>} />
            <Route path="/events-import" element={<ProtectedRoute requireRole="admin"><EventsImport /></ProtectedRoute>} />
            <Route path="/stats-import" element={<ProtectedRoute requireRole="admin"><StatsImport /></ProtectedRoute>} />
            <Route path="/roster-clean" element={<ProtectedRoute requireRole="admin"><RosterClean /></ProtectedRoute>} />
            <Route path="/admin/users" element={<ProtectedRoute requireRole="admin"><AdminUsers /></ProtectedRoute>} />
            <Route path="/vote" element={<ProtectedRoute requireRole="player"><Vote /></ProtectedRoute>} />
            <Route path="/chronicle" element={<ProtectedRoute><Chronicle /></ProtectedRoute>} />
            <Route path="/glossar" element={<ProtectedRoute><Glossar /></ProtectedRoute>} />
            <Route path="/liga" element={<ProtectedRoute><Liga /></ProtectedRoute>} />
            <Route path="/profil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute requireRole="admin"><Admin /></ProtectedRoute>} />

            {/* Predictions Feature */}
            <Route path="/predictions" element={<ProtectedRoute><PredictionsHub /></ProtectedRoute>} />
            <Route path="/predictions/live/:sessionId" element={<ProtectedRoute><PredictionsLive /></ProtectedRoute>} />
            <Route path="/predictions/stage/:sessionId" element={<ProtectedRoute><PredictionsStage /></ProtectedRoute>} />
            <Route path="/admin/predictions" element={<ProtectedRoute requireRole="admin"><PredictionsAdmin /></ProtectedRoute>} />
            <Route path="/admin/predictions/print" element={<ProtectedRoute requireRole="admin"><WarmRainPrint /></ProtectedRoute>} />
            <Route path="/admin/predictions/sessions" element={<ProtectedRoute requireRole="admin"><PredictionsHost /></ProtectedRoute>} />
            <Route path="/admin/predictions/session/:sessionId" element={<ProtectedRoute requireRole="admin"><PredictionsHost /></ProtectedRoute>} />
            <Route path="/admin/market-ops" element={<ProtectedRoute requireRole="admin"><MarketOpsPanel /></ProtectedRoute>} />
            <Route path="/media" element={<ProtectedRoute><Media /></ProtectedRoute>} />
            <Route path="/mein-abend" element={<ProtectedRoute><MeinAbend /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <AuthedNav />
          <AuthedPrompts />
          <AuthedMusic />
          <ProfileCompletionChecker />
          <WelcomeChecker />
          <EmailChecker />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
